import { useEffect } from 'react';

/**
 * Neutralises Radix / react-remove-scroll inline scrollbar compensation.
 *
 * When a Radix Dialog / Sheet / Popover opens, `react-remove-scroll` sets
 * `position: fixed` styles on <body> and adds inline `margin-right` (and
 * `padding-right` on fixed-position elements such as our header) to
 * compensate for the disappearing scrollbar. Because we already use
 * `scrollbar-gutter: stable` on <html>, the scrollbar gutter is always
 * reserved, so that compensation is what actually causes a horizontal jump.
 *
 * This hook installs a single MutationObserver at app start that watches the
 * body and any fixed header for inline `margin-right` / `padding-right`
 * additions, and forces them back to 0 immediately, preventing the visible
 * page jump.
 */
export function useScrollLockGuard() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stripCompensation = (el: HTMLElement) => {
      // Only touch the properties Radix sets; leave everything else alone.
      if (el.style.marginRight && el.style.marginRight !== '0px') {
        el.style.marginRight = '0px';
      }
      if (el.style.paddingRight && el.style.paddingRight !== '0px') {
        el.style.paddingRight = '0px';
      }
    };

    const enforceAll = () => {
      stripCompensation(document.body);
      document
        .querySelectorAll<HTMLElement>('header, [data-radix-scroll-lock]')
        .forEach(stripCompensation);
    };

    enforceAll();

    const observer = new MutationObserver(() => enforceAll());
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'data-scroll-locked'],
      subtree: false,
    });

    // Also watch the header so its inline padding-right is neutralised the
    // moment Radix sets it.
    const headerObservers: MutationObserver[] = [];
    document.querySelectorAll<HTMLElement>('header').forEach((header) => {
      const ho = new MutationObserver(() => stripCompensation(header));
      ho.observe(header, { attributes: true, attributeFilter: ['style'] });
      headerObservers.push(ho);
    });

    return () => {
      observer.disconnect();
      headerObservers.forEach((o) => o.disconnect());
    };
  }, []);
}
